'use client';
import { getLatLng } from '@utils/location';
import { getEventsByLocation } from '@v2/services/get-events-by-location';
import { getCityLatLonByName } from '@v2/services/getCityLatLonByName';
import { getEventsByCategory } from '@v2/services/getEventsByCategory';
import { AWSLocationResponse, EventResponse, TeamProps } from '@v2/types';
import { getCityPageData } from '@v2/views/city/services/getCityPageData';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EventsList } from '../components/EventsList/EventsList';
import { EventsListSkeleton } from '../components/EventsList/EventsListSkeleton';
import {
  EventResultsUiConfig,
  LeagueTeamFilterOption,
} from '../constants/eventPageUiTypes';
import { HomeAwayEventsTypes } from '../components/HomeAwayEventsTab';
import { ShowMoreButton } from '../components/ShowMoreButton';
import { shouldHideLeagueEvent } from '../util';
import { useIsConcerts } from '../use-is-concerts';
import { EventsFilters } from './EventsFilters';
import {
  appendUniqueEvents,
  buildAllLocationsHeaderText,
  getTargetVisibleEventCount,
  shouldContinueFetchingVisibleEvents,
} from './listLeagueEventsHelpers';

interface Props {
  leagueName: string;
  leagueNames?: string[];
  teamSlug?: string;
  withTitle?: boolean;
  /** League landing: large first page, hide Show more (see leagueLandingConfig.eventsList) */
  landingFullListMode?: boolean;
  /** perPage for category + location when landingFullListMode (default 100) */
  landingLargePerPage?: number;
  upcomingGamesTitleOverride?: string;
  headerDescription?: string;
  eventResultsConfig?: EventResultsUiConfig;
  initialEvents?: EventResponse[];
  initialTotalEntries?: number;
  isLeaguePage?: boolean;
  leagueTeamOptions?: LeagueTeamFilterOption[];
}

const DEFAULT_LANDING_LARGE_PER_PAGE = 100;
const ALL_LOCATIONS_FETCH_BATCH_SIZE = 20;

type ScheduleBuffer = {
  key: string;
  events: EventResponse[];
  nextPage: number;
  totalEntries: number;
  hasMorePages: boolean;
};

const HomeAwayEventstMapping = {
  [HomeAwayEventsTypes.ALL]: undefined,
  [HomeAwayEventsTypes.HOME]: true,
  [HomeAwayEventsTypes.AWAY]: false,
};

export const ListLeagueEventsWrapper = ({
  leagueName,
  leagueNames,
  teamSlug,
  withTitle = true,
  landingFullListMode = false,
  landingLargePerPage = DEFAULT_LANDING_LARGE_PER_PAGE,
  upcomingGamesTitleOverride,
  headerDescription,
  eventResultsConfig,
  initialEvents,
  initialTotalEntries = 0,
  isLeaguePage = false,
  leagueTeamOptions,
}: Props) => {
  const hasSeededInitialEvents =
    Array.isArray(initialEvents) &&
    (initialEvents.length > 0 || initialTotalEntries === 0);
  const [events, setEvents] = useState<EventResponse[] | undefined>(() =>
    hasSeededInitialEvents ? appendUniqueEvents([], initialEvents) : undefined
  );
  const [allEventsPage, setAllEventsPage] = useState(1);
  const [eventsByLocationPage, setEventsByLocationPage] = useState(1);
  const [allEventsTotalEntries, setAllEventsTotalEntries] =
    useState(initialTotalEntries);
  const [hasMoreAllEvents, setHasMoreAllEvents] = useState(
    initialTotalEntries > (initialEvents?.length ?? 0)
  );
  const [eventsByLocationTotalEntries, setEventsByLocationTotalEntries] =
    useState(0);
  const [isLocationChangeLoading, setIsLocationChangeLoading] = useState(false);
  const searchParams = useSearchParams();
  const { data, status } = useSession();

  const defaultUserLocation = data?.user?.location;

  const [location, setLocation] = useState<string>(
    defaultUserLocation || 'Miami, FL'
  );
  const [eventsByLocation, setEventsByLocation] = useState<EventResponse[]>([]);
  const [isLoadingEventsByLocation, setIsLoadingEventsByLocation] =
    useState(false);
  const [nearbyEventsError, setNearbyEventsError] = useState(false);
  const [nearbyRetryCount, setNearbyRetryCount] = useState(0);
  const nearbyEventsRequestIdRef = useRef(0);

  const [isLoadingEventsByCategory, setIsLoadingEventsByCategory] = useState(
    !hasSeededInitialEvents
  );
  const [allEventsError, setAllEventsError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const allEventsRequestIdRef = useRef(0);
  const allEventsBufferRef = useRef<ScheduleBuffer>();
  const selectedCityRef = useRef<{
    placeId: string;
    lat: number;
    lon: number;
  }>();
  const shouldUseInitialEvents = useRef(hasSeededInitialEvents);

  const [startDateFilter, setStartDateFilter] = useState<string>();
  const [endDateFilter, setEndDateFilter] = useState<string>();

  const [homeAwayFilter, setHomeAwayFilter] = useState<HomeAwayEventsTypes>();
  const [opponentId, setOpponentId] = useState<number>();
  const [selectedTeamSlug, setSelectedTeamSlug] = useState('');
  const [selectedLeagueLocation, setSelectedLeagueLocation] =
    useState<AWSLocationResponse>();
  const scheduleTeamSlug = isLeaguePage
    ? selectedTeamSlug || undefined
    : teamSlug;

  const opponentIdParam = searchParams?.get('opponentId');

  const isConcerts = useIsConcerts();
  const categoryNames = leagueNames?.length ? leagueNames : [leagueName];
  const hideNearbyEvents =
    (isLeaguePage && !!selectedLeagueLocation) ||
    (eventResultsConfig?.hideNearbyEvents ??
      eventResultsConfig?.presentation === 'team');
  const showAllEventsByDefault =
    eventResultsConfig?.showAllEventsByDefault ?? false;

  const handleGetLocation = async (loc: string) => {
    const cityData = await getCityPageData(loc);

    if (cityData) {
      setLocation(`${cityData.Municipality},`);
    }
  };

  useEffect(() => {
    if (defaultUserLocation) {
      handleGetLocation(defaultUserLocation);
    }
  }, [defaultUserLocation]);

  useEffect(() => {
    if (opponentIdParam) {
      const parsed = Number(opponentIdParam);
      if (!Number.isNaN(parsed)) {
        setOpponentId(parsed);
      }
    }
  }, [opponentIdParam]);

  useEffect(() => {
    if (shouldUseInitialEvents.current) {
      shouldUseInitialEvents.current = false;
      return;
    }

    const requestId = ++allEventsRequestIdRef.current;
    const retainedEvents = events ?? [];
    const queryKey = JSON.stringify([
      categoryNames,
      startDateFilter,
      endDateFilter,
      scheduleTeamSlug,
      selectedLeagueLocation?.PlaceId,
      homeAwayFilter,
      opponentId,
      isConcerts,
    ]);
    if (allEventsPage === 1) allEventsBufferRef.current = undefined;

    const fetchAllEvents = async () => {
      setIsLoadingEventsByCategory(true);
      if (allEventsPage === 1) setAllEventsError(false);

      try {
        const targetVisibleEvents = getTargetVisibleEventCount({
          page: allEventsPage,
          config: eventResultsConfig,
        });
        const buffer =
          allEventsBufferRef.current?.key === queryKey
            ? allEventsBufferRef.current
            : undefined;
        let currentPage = buffer?.nextPage ?? 1;
        let totalEntries = buffer?.totalEntries ?? 0;
        let bufferedVisibleEvents = buffer?.events ?? [];
        let hasMorePages = buffer?.hasMorePages ?? true;
        let selectedCity: { lat: number; lon: number } | undefined;
        if (selectedLeagueLocation) {
          if (
            selectedCityRef.current?.placeId !== selectedLeagueLocation.PlaceId
          ) {
            const place = await getLatLng(selectedLeagueLocation.PlaceId);
            if (requestId !== allEventsRequestIdRef.current) return;
            const [lon, lat] = place?.Geometry?.Point ?? [];
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
              throw new Error('Unable to find the selected location');
            }
            selectedCityRef.current = {
              placeId: selectedLeagueLocation.PlaceId,
              lat,
              lon,
            };
          }
          selectedCity = selectedCityRef.current;
        }

        while (
          shouldContinueFetchingVisibleEvents({
            visibleCount: bufferedVisibleEvents.length,
            targetVisibleCount: targetVisibleEvents,
            hasMorePages,
          })
        ) {
          const categoryEvents = await Promise.all(
            categoryNames.map(async (categoryName) => {
              if (selectedCity) {
                const response = await getEventsByLocation({
                  lat: selectedCity.lat,
                  lon: selectedCity.lon,
                  categoryName: scheduleTeamSlug ? categoryName : undefined,
                  league: categoryName,
                  teamSlug: scheduleTeamSlug,
                  startDate: startDateFilter,
                  endDate: endDateFilter,
                  page: currentPage,
                  perPage: ALL_LOCATIONS_FETCH_BATCH_SIZE,
                  sports: true,
                  rejectInvalidResponse:
                    eventResultsConfig?.presentation === 'team',
                });
                return {
                  events: response.events,
                  total_entries: response.totalEvents,
                };
              }
              return getEventsByCategory({
                categoryName,
                serverSide: false,
                page: currentPage,
                startDate: startDateFilter,
                endDate: endDateFilter,
                teamSlug: scheduleTeamSlug,
                onlyHomeGames:
                  HomeAwayEventstMapping[
                    homeAwayFilter ?? HomeAwayEventsTypes.ALL
                  ],
                opponentId,
                perPage: ALL_LOCATIONS_FETCH_BATCH_SIZE,
                concerts: isConcerts,
              });
            })
          );

          if (requestId !== allEventsRequestIdRef.current) {
            return;
          }

          if (
            categoryEvents.some((response) => !Array.isArray(response?.events))
          ) {
            throw new Error('Unable to load schedule events');
          }

          const pageEvents = categoryEvents
            .flatMap((data) => data?.events ?? [])
            .sort(
              (left, right) =>
                new Date(left.occurs_at_local).getTime() -
                new Date(right.occurs_at_local).getTime()
            );
          totalEntries = categoryEvents.reduce(
            (total, data) => total + (data?.total_entries ?? 0),
            0
          );
          const filteredPageEvents = pageEvents.filter(
            (event) => !shouldHideLeagueEvent(event.name)
          );

          bufferedVisibleEvents = appendUniqueEvents(
            bufferedVisibleEvents,
            filteredPageEvents
          );

          hasMorePages = categoryEvents.some(
            (data) =>
              data.events.length > 0 &&
              currentPage * ALL_LOCATIONS_FETCH_BATCH_SIZE <
                (data?.total_entries ?? 0)
          );
          currentPage += 1;
        }

        if (requestId !== allEventsRequestIdRef.current) {
          return;
        }

        setAllEventsError(false);
        allEventsBufferRef.current = {
          key: queryKey,
          events: bufferedVisibleEvents,
          nextPage: currentPage,
          totalEntries,
          hasMorePages,
        };
        setAllEventsTotalEntries(totalEntries);
        const visibleEvents =
          allEventsPage > 1
            ? appendUniqueEvents(retainedEvents, bufferedVisibleEvents)
            : bufferedVisibleEvents;
        setEvents(visibleEvents.slice(0, targetVisibleEvents));
        setHasMoreAllEvents(
          visibleEvents.length > targetVisibleEvents || hasMorePages
        );
      } catch (e) {
        console.error(e);

        if (requestId !== allEventsRequestIdRef.current) {
          return;
        }

        if (allEventsPage === 1) {
          setAllEventsTotalEntries(0);
          setEvents([]);
          setHasMoreAllEvents(false);
        }
        setAllEventsError(true);
      } finally {
        if (requestId === allEventsRequestIdRef.current) {
          setIsLoadingEventsByCategory(false);
        }
      }
    };

    fetchAllEvents();
    return () => {
      allEventsRequestIdRef.current += 1;
    };
  }, [
    allEventsPage,
    startDateFilter,
    endDateFilter,
    scheduleTeamSlug,
    selectedLeagueLocation,
    homeAwayFilter,
    opponentId,
    leagueName,
    leagueNames,
    isConcerts,
    retryCount,
  ]);

  useEffect(() => {
    const requestId = ++nearbyEventsRequestIdRef.current;
    if (status === 'loading' || hideNearbyEvents) {
      return;
    }
    async function fetchEventsByLocation() {
      setIsLoadingEventsByLocation(true);
      if (eventsByLocationPage === 1) setNearbyEventsError(false);

      try {
        const cityData = await getCityLatLonByName(location);

        if (!cityData) throw new Error('Unable to find the nearby location');
        const response = await getEventsByLocation({
          serverSide: false,
          lat: cityData?.Geometry.lat,
          lon: cityData?.Geometry.lon,
          categoryName: leagueName,
          withVenueImages: false,
          perPage: landingFullListMode ? landingLargePerPage : 3,
          page: eventsByLocationPage,
          sports: !isConcerts,
          concerts: isConcerts,
          startDate: startDateFilter,
          endDate: endDateFilter,
          teamSlug: scheduleTeamSlug,
          onlyHomeGames:
            HomeAwayEventstMapping[homeAwayFilter ?? HomeAwayEventsTypes.ALL],
          opponentId,
          league: leagueName,
          rejectInvalidResponse: eventResultsConfig?.presentation === 'team',
        });
        if (requestId !== nearbyEventsRequestIdRef.current) return;
        setNearbyEventsError(false);
        setEventsByLocationTotalEntries(response.totalEvents);
        setEventsByLocation((old) => {
          // Only append events when paginating (page > 1), otherwise replace
          if (eventsByLocationPage > 1 && old?.length) {
            return appendUniqueEvents(old, response.events);
          }
          return response.events;
        });
      } catch (e) {
        console.error(e);
        if (requestId === nearbyEventsRequestIdRef.current) {
          setNearbyEventsError(true);
        }
      } finally {
        if (requestId === nearbyEventsRequestIdRef.current) {
          setIsLoadingEventsByLocation(false);
          setIsLocationChangeLoading(false);
        }
      }
    }

    fetchEventsByLocation();
    return () => {
      nearbyEventsRequestIdRef.current += 1;
    };
  }, [
    data,
    status,
    location,
    leagueName,
    eventsByLocationPage,
    startDateFilter,
    endDateFilter,
    scheduleTeamSlug,
    homeAwayFilter,
    opponentId,
    landingFullListMode,
    landingLargePerPage,
    hideNearbyEvents,
    isConcerts,
    nearbyRetryCount,
  ]);

  const onLocationChange = useCallback(
    (nextLocation?: AWSLocationResponse) => {
      if (isLeaguePage) {
        if (nextLocation?.PlaceId === selectedLeagueLocation?.PlaceId) return;
        setSelectedLeagueLocation(nextLocation);
        setEvents([]);
        setIsLoadingEventsByCategory(true);
      } else if (nextLocation) {
        const label =
          `${nextLocation.Text} ${nextLocation.Region ?? ''}`.trim();
        if (label === location) return;
        setLocation(label);
      }
      setEventsByLocation([]);
      setEventsByLocationTotalEntries(0);
      setIsLocationChangeLoading(true);
      setAllEventsPage(1);
      setEventsByLocationPage(1);
    },
    [isLeaguePage, selectedLeagueLocation, location]
  );

  const onTeamChange = useCallback(
    (slug: string) => {
      if (slug === selectedTeamSlug) return;
      setSelectedTeamSlug(slug);
      setEvents([]);
      setEventsByLocation([]);
      setAllEventsPage(1);
      setEventsByLocationPage(1);
      setIsLocationChangeLoading(true);
      setIsLoadingEventsByCategory(true);
    },
    [selectedTeamSlug]
  );

  const onDateChange = useCallback(
    (startDate: string, endDate: string) => {
      const nextEndDate = endDate || undefined;
      const nextStartDate = nextEndDate ? startDate || undefined : undefined;
      if (nextStartDate === startDateFilter && nextEndDate === endDateFilter)
        return;
      setStartDateFilter(nextStartDate);
      setEndDateFilter(nextEndDate);
      setEventsByLocation([]);
      setEvents([]);
      setAllEventsPage(1);
      setEventsByLocationPage(1);
      setIsLocationChangeLoading(true);
      setIsLoadingEventsByCategory(true);
    },
    [startDateFilter, endDateFilter]
  );

  const onHomeAwayChange = useCallback(
    (tab: HomeAwayEventsTypes) => {
      if (tab === (homeAwayFilter ?? HomeAwayEventsTypes.ALL)) return;
      setAllEventsPage(1);
      setEventsByLocationPage(1);
      setHomeAwayFilter(tab);
      setEventsByLocation([]);
      setIsLocationChangeLoading(true);
      setIsLoadingEventsByCategory(true);
    },
    [homeAwayFilter]
  );

  const onOpponentChange = useCallback(
    (opponent?: TeamProps) => {
      if (opponent?.teamId === opponentId) return;
      setAllEventsPage(1);
      setEventsByLocationPage(1);
      setOpponentId(opponent?.teamId);
      setIsLocationChangeLoading(true);
      setIsLoadingEventsByCategory(true);
    },
    [opponentId]
  );

  const visibleAllEventsCount = events?.length ?? 0;
  const allLocationsHeaderText = buildAllLocationsHeaderText({
    visibleCount: visibleAllEventsCount,
    totalEntries: allEventsTotalEntries,
    config: eventResultsConfig,
  }).replace(
    'in all locations',
    selectedLeagueLocation
      ? `near ${selectedLeagueLocation.Text}`
      : 'in all locations'
  );

  return (
    <div>
      <div>
        {withTitle && (
          <h2
            className={
              headerDescription
                ? 'mb-2 text-xl font-bold leading-[30px] md:text-2xl md:leading-8'
                : 'mb-6 text-2xl font-bold'
            }
          >
            {upcomingGamesTitleOverride ??
              (isConcerts ? 'Upcoming Concerts' : 'Upcoming Games')}
          </h2>
        )}
        {headerDescription && (
          <p className="mb-6 text-sm font-medium leading-5 text-gray-600">
            {headerDescription}
          </p>
        )}
        <EventsFilters
          onLocationChange={onLocationChange}
          onDateChange={onDateChange}
          isTeamPage={Boolean(teamSlug) && !eventResultsConfig?.hideTeamFilters}
          leagueSlug={leagueName}
          teamSlug={teamSlug}
          onHomeAwayChange={onHomeAwayChange}
          homeAwayValue={homeAwayFilter ?? HomeAwayEventsTypes.ALL}
          onOpponentChange={onOpponentChange}
          eventResultsConfig={eventResultsConfig}
          isLeaguePage={isLeaguePage}
          leagueTeamOptions={leagueTeamOptions}
          selectedTeamSlug={selectedTeamSlug}
          onTeamChange={isLeaguePage ? onTeamChange : undefined}
        />
      </div>
      <div className="flex flex-col gap-y-5">
        {!hideNearbyEvents && (
          <div>
            {isLocationChangeLoading ||
            (isLoadingEventsByLocation && eventsByLocationPage === 1) ? (
              <EventsListSkeleton numberOfLines={3} />
            ) : nearbyEventsError && eventsByLocationPage === 1 ? (
              <div
                role="alert"
                className="mt-5 border-b border-gray-200 py-3 text-sm text-gray-600"
              >
                <p>We couldn&apos;t load nearby events.</p>
                <button
                  type="button"
                  onClick={() => setNearbyRetryCount((count) => count + 1)}
                  className="mt-2 min-h-11 rounded-lg border border-gray-300 bg-white px-4 font-semibold text-gray-800"
                >
                  Try nearby events again
                </button>
              </div>
            ) : (
              <>
                <EventsList
                  className="mt-5"
                  identifier="eventsByLocation"
                  headerText={`${eventsByLocationTotalEntries} events near ${location}`}
                  events={eventsByLocation}
                  layout={
                    eventsByLocation.length === 0
                      ? eventResultsConfig?.layout
                      : undefined
                  }
                  itemListName="nearby_events"
                  itemCategory={leagueName}
                />
                {nearbyEventsError && (
                  <p role="alert" className="mt-3 text-sm text-gray-600">
                    We couldn&apos;t load more nearby events. Please try again.
                  </p>
                )}
                {!landingFullListMode &&
                  Boolean(
                    eventsByLocation?.length < eventsByLocationTotalEntries &&
                    eventsByLocationTotalEntries
                  ) && (
                    <ShowMoreButton
                      isLoading={isLoadingEventsByLocation}
                      label={
                        nearbyEventsError
                          ? 'Try nearby events again'
                          : undefined
                      }
                      onShowMoreClick={() => {
                        if (nearbyEventsError) {
                          setNearbyRetryCount((count) => count + 1);
                        } else {
                          setEventsByLocationPage((old) => old + 1);
                        }
                      }}
                    />
                  )}
              </>
            )}
          </div>
        )}

        <div>
          {isLoadingEventsByCategory && allEventsPage === 1 ? (
            <EventsListSkeleton numberOfLines={15} />
          ) : allEventsError &&
            allEventsPage === 1 &&
            eventResultsConfig?.presentation === 'team' ? (
            <div
              role="alert"
              className="rounded-xl border border-gray-200 bg-gray-50 p-6 md:p-8"
            >
              <h3 className="text-lg font-semibold leading-7 text-gray-800">
                We couldn&apos;t load events
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                Please try again to see the latest availability.
              </p>
              <button
                type="button"
                onClick={() => setRetryCount((count) => count + 1)}
                className="mt-4 rounded-lg border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-800"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              <EventsList
                identifier="allEvents"
                headerText={allLocationsHeaderText}
                events={events ?? []}
                shouldReturnEmpty={false}
                actionLabel={eventResultsConfig?.actionLabel}
                layout={eventResultsConfig?.layout}
                itemListName={
                  teamSlug ? 'team_upcoming_events' : 'league_all_events'
                }
                itemCategory={leagueName}
              />
              {allEventsError && allEventsPage > 1 && (
                <p role="alert" className="mt-3 text-sm text-gray-600">
                  We couldn&apos;t load more events. Please try again.
                </p>
              )}
              {Boolean(hasMoreAllEvents) && !showAllEventsByDefault && (
                <ShowMoreButton
                  isLoading={isLoadingEventsByCategory}
                  label={allEventsError ? 'Try again' : undefined}
                  className={
                    eventResultsConfig?.presentation === 'team'
                      ? 'mt-6 rounded-lg border border-gray-200 bg-white py-3'
                      : undefined
                  }
                  onShowMoreClick={() => {
                    if (allEventsError) {
                      setRetryCount((count) => count + 1);
                    } else {
                      setAllEventsPage((old) => old + 1);
                    }
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
